import { defineStore } from "pinia";
import { getShortcuts, saveShortcuts } from "@/api/shortcuts";
import { useAuthStore } from "./authStore";
import { SHORTCUTS_CONFIG } from "@/config/shortcuts";

export const useShortcutsStore = defineStore("shortcuts", {
  state: () => ({
    selectedShortcuts: ["mes-documents", "calcul-ration", "mes-factures"],
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * Raccourcis sélectionnés avec leurs métadonnées complètes
     */
    selectedShortcutsWithConfig: state => {
      return state.selectedShortcuts
        .filter(shortcutId => shortcutId !== null)
        .map(shortcutId => {
          const config = SHORTCUTS_CONFIG.find(s => s.id === shortcutId);
          return config || null;
        })
        .filter(Boolean);
    },

    /**
     * Raccourcis disponibles (non sélectionnés)
     */
    availableShortcuts: state => {
      return SHORTCUTS_CONFIG.filter(shortcut => !state.selectedShortcuts.includes(shortcut.id));
    },

    /**
     * Vérifie si on peut sélectionner plus de raccourcis
     */
    canSelectMore: state => {
      const selectedCount = state.selectedShortcuts.filter(s => s !== null).length;
      return selectedCount < 3;
    },

    /**
     * Vérifie si un raccourci est sélectionné
     */
    isShortcutSelected: state => shortcutId => {
      return state.selectedShortcuts.includes(shortcutId);
    },
  },

  actions: {
    /**
     * Récupère les raccourcis depuis l'API
     */
    async fetchShortcuts() {
      const authStore = useAuthStore();
      const customerId = authStore.user?.id;

      if (!customerId) {
        console.warn("Aucun ID utilisateur disponible pour récupérer les raccourcis");
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await getShortcuts(customerId);

        if (response && response.value && Array.isArray(response.value)) {
          this.selectedShortcuts = response.value
            .filter(shortcut => SHORTCUTS_CONFIG.some(config => config.id === shortcut))
            .slice(0, 3); // Limiter à 3 raccourcis
          if (this.selectedShortcuts.length === 0) {
            this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
          }
        } else {
          this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
        }

        localStorage.setItem("shortcuts", JSON.stringify(this.selectedShortcuts));
      } catch (error) {
        this.error = error.message;

        const localShortcuts = localStorage.getItem("shortcuts");
        if (localShortcuts) {
          try {
            const parsed = JSON.parse(localShortcuts);
            this.selectedShortcuts = parsed
              .filter(shortcut => SHORTCUTS_CONFIG.some(config => config.id === shortcut))
              .slice(0, 3);
            if (this.selectedShortcuts.length === 0) {
              this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
            }
          } catch (e) {
            this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
          }
        } else {
          this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Sauvegarde les raccourcis via l'API
     */
    async saveShortcutsToAPI() {
      const authStore = useAuthStore();
      const customerId = authStore.user?.id;

      if (!customerId) {
        console.warn("Aucun ID utilisateur disponible pour sauvegarder les raccourcis");
        return;
      }

      try {
        await saveShortcuts(customerId, this.selectedShortcuts);

        localStorage.setItem("shortcuts", JSON.stringify(this.selectedShortcuts));
      } catch (error) {
        this.error = error.message;
        console.error("Erreur lors de la sauvegarde des raccourcis:", error);
        localStorage.setItem("shortcuts", JSON.stringify(this.selectedShortcuts));
      }
    },

    /**
     * Bascule l'état d'un raccourci (sélectionné/non sélectionné)
     */
    async toggleShortcut(shortcutId) {
      const isSelected = this.selectedShortcuts.includes(shortcutId);

      if (isSelected) {
        const index = this.selectedShortcuts.indexOf(shortcutId);
        this.selectedShortcuts[index] = null;
      } else {
        if (this.canSelectMore) {
          const firstNullIndex = this.selectedShortcuts.indexOf(null);
          if (firstNullIndex !== -1) {
            this.selectedShortcuts[firstNullIndex] = shortcutId;
          } else {
            if (this.selectedShortcuts.length < 3) {
              this.selectedShortcuts.push(shortcutId);
            }
          }
        } else {
          console.warn("Maximum de 3 raccourcis atteint");
          return;
        }
      }

      this.selectedShortcuts = this.selectedShortcuts.filter(s => s !== null);
      await this.saveShortcutsToAPI();
    },

    /**
     * Réinitialise le store
     */
    reset() {
      this.selectedShortcuts = ["mes-documents", "calcul-ration", "mes-factures"];
      this.loading = false;
      this.error = null;
      localStorage.removeItem("shortcuts");
    },
  },

  persist: {
    key: "shortcuts-store",
    storage: localStorage,
    paths: ["selectedShortcuts"],
  },
});
